import { enumToCapitalizedString, REGEX } from '@admiin-com/ds-common';
import {
  CreateEntityInput,
  EntityType,
  GSTReportingPeriod,
} from '@admiin-com/ds-graphql';
import {
  WBBox,
  WBCollapse,
  WBSelect,
  WBStack,
  WBTextFieldV2,
  WBTypography,
} from '@admiin-com/ds-web';
import React, { useMemo } from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AutoCompleteLookupV2, {
  ABNResult,
} from '../../../components/AutoCompleteLookup/AbnAutoCompleteLookupV2';
import {
  InputLabel,
  styled,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import EntityAddressForm from '../../../components/EntityAddressForm/EntityAddressForm';

export interface EntityCreateFormProps {
  isOnboarding?: boolean;
  entityTypes?: EntityType[];
  disabled?: boolean;
  noAddress?: boolean;
  onAbnLookupStart?: () => void;
  onAbnLookupEnd?: () => void;
  type?: 'ENTITY' | 'CONTACT' | 'CLIENT';
  noType?: boolean;
  ocrCreate?: boolean;
  prefix?: string;
}

export function EntityCreateForm({
  isOnboarding = false,
  onAbnLookupStart,
  onAbnLookupEnd,
  disabled = false,
  noAddress = false,
  type = 'CONTACT',
  noType,
  ocrCreate = false,
  prefix = '',
}: EntityCreateFormProps) {
  const {
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
    getFieldState,
  } = useFormContext<FieldValues>();
  const { t } = useTranslation();

  // Build the entity field path based on prefix
  const entityFieldPath = prefix ? `${prefix}entity` : 'entity';
  const entity = watch(entityFieldPath) as CreateEntityInput | undefined;

  const [abnStatus, setABNStatus] = React.useState<'HasAbn' | 'NoAbn'>(
    'HasAbn'
  );
  const hasABN = abnStatus === 'HasAbn';

  const [entitySearched, setEntitySearched] = React.useState<
    ABNResult | undefined
  >();
  const isIndividual = entity?.type === EntityType.INDIVIDUAL && !hasABN;
  const isSoleTrader = entity?.type === EntityType.SOLE_TRADER;

  // Helper function to get nested errors using react-hook-form's getFieldState
  const getEntityError = (field: string) => {
    const fullFieldPath = prefix
      ? `${prefix}entity.${field}`
      : `entity.${field}`;
    const { error } = getFieldState(fullFieldPath);
    return error;
  };

  const resetForm = () => {
    clearErrors();
    setValue(entityFieldPath as any, {
      taxNumber: '',
      companyNumber: '',
      type: '' as any,
      name: '',
    });
    setEntitySearched(undefined);
  };
  React.useEffect(() => {
    if (!disabled && !hasABN) {
      resetForm();
    }
  }, [hasABN, disabled]);

  React.useEffect(() => {
    if (!hasABN && onAbnLookupEnd) onAbnLookupEnd();
  }, [hasABN]);

  const inputs = useMemo(
    () => ({
      entity: {
        type: {
          label: t('type', { ns: 'common' }),
          name: `${entityFieldPath}.type` as any,
          type: 'select',
          placeholder: t('typePlaceholder', { ns: 'onboarding' }),
          rules: { required: t('typeRequired', { ns: 'onboarding' }) },
        },
        taxNumber: {
          label: t(type === 'ENTITY' ? 'searchEntity' : 'searchEntity', {
            ns: 'onboarding',
          }),
          name: `${entityFieldPath}.taxNumber` as any,
          type: 'text',
          placeholder: t('abnPlaceholder', { ns: 'onboarding' }),
          defaultValue: '',
          rules:
            (hasABN && type !== 'CONTACT') || ocrCreate
              ? {
                  required: t('abnRequired', { ns: 'onboarding' }),
                }
              : {},
        },
        name: {
          label: t('businessName', { ns: 'onboarding' }),
          name: `${entityFieldPath}.name` as any,
          type: 'text',
          placeholder: t('businessNamePlaceholder', { ns: 'onboarding' }),
          defaultValue: '',
          rules:
            type !== 'CONTACT'
              ? {
                  required: t('businessNameRequired', { ns: 'onboarding' }),
                }
              : {},
        },
        companyNumber: {
          label: t('companyNumber', { ns: 'common' }),
          name: `${entityFieldPath}.companyNumber` as any,
          type: 'text',
          placeholder: t('companyNumberPlaceholder', { ns: 'common' }),
          defaultValue: '',
          // Optional field, no rules
        },
        website: {
          label: t('website', { ns: 'common' }),
          name: `${entityFieldPath}.website` as any,
          type: 'text',
          placeholder: t('websitePlaceholder', { ns: 'common' }),
          defaultValue: '',
          rules: {
            validate: (value: string | null | undefined) =>
              value === null ||
              value === undefined ||
              value === '' ||
              REGEX.URL.test(value) ||
              t('websiteInvalid', { ns: 'common' }),
          },
        },
        firstName: {
          label: t('firstNameTitle', { ns: 'common' }),
          name: `${entityFieldPath}.firstName` as any,
          type: 'text',
          placeholder: t('firstNamePlaceholder', { ns: 'common' }),
          defaultValue: '',
          rules: { required: t('firstNameRequired', { ns: 'common' }) },
        },
        lastName: {
          label: t('lastNameTitle', { ns: 'common' }),
          name: `${entityFieldPath}.lastName` as any,
          type: 'text',
          placeholder: t('lastNamePlaceholder', { ns: 'common' }),
          defaultValue: '',
          rules: { required: t('lastNameRequired', { ns: 'common' }) },
        },
      },
    }),
    [t, hasABN, type, ocrCreate, entityFieldPath]
  );

  return (
    <>
      {!disabled && type === 'CLIENT' && (
        <WBStack sx={{ mt: 2 }} direction="row" spacing={2}>
          <ToggleButtonGroup
            value={abnStatus}
            onChange={(event: React.MouseEvent<HTMLElement>, newValue: any) => {
              if (newValue) setABNStatus(newValue);
            }}
            exclusive
          >
            <AbnSelector value="HasAbn">
              {t('hasABN', { ns: 'onboarding' })}
            </AbnSelector>
            <AbnSelector value="NoAbn">
              {t('noABN', { ns: 'onboarding' })}
            </AbnSelector>
          </ToggleButtonGroup>
        </WBStack>
      )}
      {!noType && !hasABN && !disabled && (
        <Controller
          control={control}
          name={inputs.entity.type.name}
          rules={inputs.entity.type.rules}
          defaultValue={'' as any}
          disabled={disabled}
          render={({ field }) => (
            <WBSelect
              {...field}
              options={[
                ...Object.keys(EntityType)
                  .filter(
                    (type) =>
                      (!isOnboarding && type !== EntityType.BPAY) ||
                      (isOnboarding && type !== EntityType.BPAY)
                  )
                  .map((value) => ({
                    label: t(value, { ns: 'common' }),
                    value: value,
                  })),
              ]}
              label={inputs.entity.type.label}
              placeholder={inputs.entity.type.placeholder}
              error={!!getEntityError('type')}
              helperText={
                ((getEntityError('type') &&
                  (getEntityError('type') as any).message) as string) || ''
              }
            />
          )}
        />
      )}
      <>
        {hasABN && (
          <Controller
            control={control}
            name={inputs.entity.taxNumber.name}
            rules={inputs.entity.taxNumber.rules}
            defaultValue={inputs.entity.taxNumber.defaultValue}
            render={({ field, fieldState }) => (
              <WBBox sx={{ mt: 2 }}>
                {!disabled && (
                  <AutoCompleteLookupV2
                    {...field}
                    label={inputs.entity.taxNumber.label}
                    placeholder={inputs.entity.taxNumber.placeholder}
                    disabled={disabled}
                    noPopupIcon
                    onChange={(value) => {
                      console.log(field.ref);
                    }}
                    onLoading={(loading: boolean) => {
                      if (loading) {
                        if (onAbnLookupStart) onAbnLookupStart();
                      } else if (onAbnLookupEnd) onAbnLookupEnd();
                    }}
                    onSearch={(value) => {
                      setEntitySearched(value);
                      setValue(`${entityFieldPath}.name` as any, value.name);
                      setValue(
                        `${entityFieldPath}.taxNumber` as any,
                        value.abn
                      );
                      setValue(
                        `${entityFieldPath}.companyNumber` as any,
                        value.acn
                      );
                      setValue(
                        `${entityFieldPath}.type` as any,
                        value.type as EntityType
                      );
                      // Set GST registered based on ABN lookup result
                      const isGstRegistered = !!(value.gst && value.gst.trim());
                      setValue(
                        `${entityFieldPath}.gstRegistered` as any,
                        isGstRegistered
                      );
                      setValue(
                        `${entityFieldPath}.gstReportingPeriod` as any,
                        isGstRegistered ? GSTReportingPeriod.QUARTERLY : null
                      );
                      if (onAbnLookupEnd) onAbnLookupEnd();
                      if (value?.abn === '') {
                        clearErrors();
                      }
                    }}
                    onError={(error) => {
                      console.error('error: ', error);
                      resetForm();
                      if (error.message === 'Network Error') {
                        if (onAbnLookupEnd) onAbnLookupEnd();
                      } else {
                        setError(`${entityFieldPath}.taxNumber` as any, {
                          type: 'validate',
                          message: t(error?.message, { ns: 'onboarding' }),
                        });
                      }
                    }}
                    renderProps={{
                      error: fieldState.invalid,
                      helperText: fieldState.error?.message,
                    }}
                  />
                )}
                <WBCollapse in={!!entitySearched || !!entity?.name}>
                  {(entitySearched?.name || entity?.name) && (
                    <WBBox mt={1}>
                      <InputLabel>
                        {t('businessName', { ns: 'onboarding' })}
                      </InputLabel>
                      <WBTypography sx={{ mt: 1 }}>
                        {entitySearched?.name || entity?.name}
                      </WBTypography>
                    </WBBox>
                  )}
                  <WBBox
                    mt={1}
                    display="flex"
                    flexDirection={['row', 'column']}
                  >
                    {(entitySearched?.abn || entity?.taxNumber) && (
                      <WBBox flex={1} mt={1} mr={[2, 0]}>
                        <InputLabel>
                          {t('abn', { ns: 'onboarding' })}
                        </InputLabel>
                        <WBTypography sx={{ mt: 1 }}>
                          {entitySearched?.abn || entity?.taxNumber}
                        </WBTypography>
                      </WBBox>
                    )}
                    {(entitySearched?.acn || entity?.companyNumber) && (
                      <WBBox flex={1} mt={1}>
                        <InputLabel>
                          {t('acn', { ns: 'onboarding' })}
                        </InputLabel>
                        <WBTypography sx={{ mt: 1 }}>
                          {entitySearched?.acn || entity?.companyNumber}
                        </WBTypography>
                      </WBBox>
                    )}
                  </WBBox>
                  {(entitySearched?.type || entity?.type) && (
                    <WBBox mt={1}>
                      <InputLabel>
                        {t('entityType', { ns: 'onboarding' })}
                      </InputLabel>
                      <WBTypography sx={{ mt: 1 }}>
                        {enumToCapitalizedString(
                          entitySearched?.type || entity?.type
                        )}
                      </WBTypography>
                    </WBBox>
                  )}
                </WBCollapse>
              </WBBox>
            )}
          />
        )}
        {!hasABN && !isIndividual && (
          <>
            <Controller
              control={control}
              name={inputs.entity.name.name}
              rules={inputs.entity.name.rules}
              defaultValue={inputs.entity.name.defaultValue}
              render={({ field }) => (
                <WBBox sx={{ mt: 2 }}>
                  <WBTextFieldV2
                    {...field}
                    label={inputs.entity.name.label}
                    disabled={disabled}
                    type={inputs.entity.name.type}
                    placeholder={inputs.entity.name.placeholder}
                    error={
                      !!(
                        getEntityError('name') &&
                        getEntityError('name')?.message
                      )
                    }
                    helperText={
                      ((getEntityError('name') &&
                        getEntityError('name')?.message) as string) || ''
                    }
                    margin="dense"
                  />
                </WBBox>
              )}
            />
            {!isSoleTrader && (
              <Controller
                control={control}
                name={inputs.entity.companyNumber.name}
                defaultValue={inputs.entity.companyNumber.defaultValue}
                render={({ field }) => (
                  <WBBox sx={{ mt: 2 }}>
                    <WBTextFieldV2
                      {...field}
                      label={inputs.entity.companyNumber.label}
                      disabled={disabled}
                      type={inputs.entity.companyNumber.type}
                      placeholder={inputs.entity.companyNumber.placeholder}
                      error={
                        !!(
                          getEntityError('companyNumber') &&
                          getEntityError('companyNumber')?.message
                        )
                      }
                      helperText={
                        ((getEntityError('companyNumber') &&
                          getEntityError('companyNumber')
                            ?.message) as string) || ''
                      }
                      margin="dense"
                    />
                  </WBBox>
                )}
              />
            )}
            <Controller
              control={control}
              name={inputs.entity.website.name}
              defaultValue={inputs.entity.website.defaultValue}
              rules={inputs.entity.website.rules}
              render={({ field }) => (
                <WBBox sx={{ mt: 2 }}>
                  <WBTextFieldV2
                    {...field}
                    label={inputs.entity.website.label}
                    disabled={disabled}
                    type={inputs.entity.website.type}
                    placeholder={inputs.entity.website.placeholder}
                    error={
                      !!(
                        getEntityError('website') &&
                        (getEntityError('website') as any).message
                      )
                    }
                    helperText={
                      ((getEntityError('website') &&
                        (getEntityError('website') as any)
                          .message) as string) || ''
                    }
                    margin="dense"
                  />
                </WBBox>
              )}
            />
          </>
        )}
      </>
      {/*)}*/}
      {noAddress || disabled ? null : (
        <WBBox mt={2}>
          <EntityAddressForm
            onlyAustralia={hasABN}
            optional={isIndividual}
            disableManual={type === 'ENTITY'}
            prefix={prefix}
          />
        </WBBox>
      )}
    </>
  );
}

const AbnSelector = styled(ToggleButton)(({ theme }) => {
  const mode = theme.palette.mode;
  return {
    '&.Mui-selected': {
      backgroundColor:
        mode === 'dark'
          ? theme.palette.common.white
          : theme.palette.common.black,
      color:
        mode === 'dark'
          ? theme.palette.common.black
          : theme.palette.common.white,
    },

    '&:disabled': {
      border: 0,
    },
    fontWeight: 'bold',

    border: 0,
    marginRight: theme.spacing(1),
    '&:last-child': {
      marginRight: 0,
    },
  };
});

export default EntityCreateForm;
